/**
 * AD/Scheduler → Org Teams configurator (nav renamed from "Teams" 2026-10-13). Three condensed click-to-edit surfaces:
 *   • Sport Teams — the org's sports (name / season start month / order / active).
 *   • Team Levels — per season, per sport: roster teams (level + free-text variant
 *     → auto canonical name), player counts. Add creates OL roster + WP term +
 *     own-squad team. New season → "Copy from <previous>".
 *   • Team Costs  — per team per season: inline Season cost ($) + max age.
 * OL builds every slug/title/WP; the AD never sees a slug.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Layers, DollarSign, Trophy, Plus, Trash2, Copy, Network, MapPin } from 'lucide-react';
import { InlineText, InlineSelect, SearchBox, matchesSearch, thCls, tdCls } from './adStaffShared';
import { AgeCutoffControl, SchoolYearStartControl, TeamDivisions } from './OrgTeamExtras';
import OrgHomeVenues from './OrgHomeVenues';

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'].map((m, i) => ({ value: String(i + 1), label: m }));
const TABS = [
  { key: 'sports', label: 'Sport Teams', icon: Trophy },
  { key: 'levels', label: 'Team Levels', icon: Layers },
  { key: 'costs',  label: 'Team Costs',  icon: DollarSign },
  { key: 'venues', label: 'Venues', icon: MapPin },
  { key: 'divisions', label: 'Divisions', icon: Network },
];

export default function OrgTeamsPanel({ orgId, isDark }) {
  const [active, setActive] = useState('sports');
  const [jump, setJump] = useState(null);  // {season, sport} handoff Costs→Levels
  if (!orgId) return null;
  const navBg = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const inactive = isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700/40' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100';
  const activeCls = isDark ? 'bg-amber-600/20 text-amber-400 border-amber-500/40' : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <div className="space-y-4" data-testid="org-teams-panel">
      <div className={`flex flex-wrap items-center gap-1 p-1.5 rounded-lg border ${navBg}`} data-testid="org-teams-tabs">
        {TABS.map(t => {
          const Icon = t.icon; const on = active === t.key;
          return (
            <Button key={t.key} variant="ghost" size="sm" onClick={() => setActive(t.key)}
                    className={`h-9 px-4 text-sm font-medium border ${on ? activeCls : 'border-transparent ' + inactive}`}
                    data-testid={`org-teams-tab-${t.key}`}>
              <Icon className="h-4 w-4 mr-2" />{t.label}
            </Button>
          );
        })}
      </div>
      {active === 'sports' && <SportTeams orgId={orgId} isDark={isDark} />}
      {active === 'levels' && <TeamLevels orgId={orgId} isDark={isDark} initial={jump} />}
      {active === 'costs' && <TeamCosts orgId={orgId} isDark={isDark}
                                        onGoLevels={(s, sp) => { setJump({ season: s, sport: sp }); setActive('levels'); }} />}
      {active === 'divisions' && <TeamDivisions orgId={orgId} isDark={isDark} />}
      {active === 'venues' && <OrgHomeVenues orgId={orgId} isDark={isDark} />}
    </div>
  );
}

const wrap = (isDark) => isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
const rowCls = (isDark) => isDark ? 'group border-t border-slate-700/50 hover:bg-slate-700/20' : 'group border-t border-slate-100 hover:bg-slate-50';

/* ── Sport Teams ─────────────────────────────────────────────── */
function SportTeams({ orgId, isDark }) {
  const [sports, setSports] = useState(null);
  const [q, setQ] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/organizations/${orgId}/sports`);
    data.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999) || (a.name || '').localeCompare(b.name || ''));
    setSports(data);
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  const save = async (id, field, val) => { await axios.put(`${API}/api/sports/${id}`, { [field]: field === 'name' ? val : (parseInt(val, 10) || 0) }); load(); };
  const add = async () => { if (!name.trim()) return; setBusy(true); try { await axios.post(`${API}/api/organizations/${orgId}/sports`, { name: name.trim(), org_id: orgId }); setName(''); load(); } finally { setBusy(false); } };
  const del = async (s) => { if (!window.confirm(`Delete sport "${s.name}"?`)) return; try { await axios.delete(`${API}/api/sports/${s.id}`); load(); } catch (e) { window.alert(e.response?.data?.detail || 'Cannot delete'); } };
  if (!sports) return <Spin />;
  const rows = sports.filter(s => matchesSearch({ role_title: s.name }, q));
  return (
    <Card className={wrap(isDark)}><CardContent className="p-4 space-y-3" data-testid="sport-teams">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className={`text-base font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Sport Teams</div>
        <SearchBox value={q} onChange={setQ} isDark={isDark} placeholder="Search sports…" testid="sport-teams-search" />
      </div>
      <SchoolYearStartControl orgId={orgId} isDark={isDark} />
      <table className="w-full text-sm border-collapse"><thead><tr>
        <th className={thCls(isDark)}>Sport</th>
        <th className={thCls(isDark)} title="Month (1-12) the sport's season starts. Used only to detect spring sports for academic-year labeling.">Season start month</th>
        <th className={thCls(isDark)}>Display order</th>
        <th className={thCls(isDark) + ' w-6'}></th>
      </tr></thead><tbody>
        {rows.map(s => (
          <tr key={s.id} className={rowCls(isDark)} data-testid={`sport-row-${s.id}`}>
            <td className={tdCls + ' font-medium ' + (isDark ? 'text-amber-400' : 'text-amber-700') + ' min-w-[160px]'}>
              <InlineText value={s.name} isDark={isDark} testid={`sport-name-${s.id}`} onSave={v => save(s.id, 'name', v)} /></td>
            <td className={tdCls + ' min-w-[130px]'}><InlineSelect value={s.season_start_month != null ? String(s.season_start_month) : ''} label={s.season_start_month != null ? (MONTHS[s.season_start_month - 1] || {}).label : ''} options={MONTHS} placeholder="—" isDark={isDark} testid={`sport-month-${s.id}`} onSave={v => save(s.id, 'season_start_month', v)} /></td>
            <td className={tdCls + ' min-w-[90px]'}><InlineText value={s.display_order != null ? String(s.display_order) : ''} type="number" placeholder="—" isDark={isDark} testid={`sport-order-${s.id}`} onSave={v => save(s.id, 'display_order', v)} /></td>
            <td className={tdCls}><button onClick={() => del(s)} title="Delete" data-testid={`sport-del-${s.id}`} className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button></td>
          </tr>
        ))}
        <tr><td className="px-2 py-2" colSpan={4}>
          <div className="flex items-center gap-2">
            <input value={name} onChange={e => setName(e.target.value)} placeholder="New sport name (e.g. Soccer)" data-testid="sport-add-name"
                   className={`h-8 text-sm rounded px-2 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`} />
            <Button size="sm" className="h-8 bg-amber-600 hover:bg-amber-700 text-white" disabled={busy || !name.trim()} onClick={add} data-testid="sport-add-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" />Add sport</>}</Button>
          </div>
        </td></tr>
      </tbody></table>
    </CardContent></Card>
  );
}

/* ── shared season/sport hooks ───────────────────────────────── */
function useSeasonsSports(orgId) {
  const [seasons, setSeasons] = useState([]);
  const [sports, setSports] = useState([]);
  const [levels, setLevels] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/ad-teams/${orgId}/season-options`).then(r => setSeasons(r.data.data || []));
    axios.get(`${API}/api/ol-canonical/roster-form-options/${orgId}`).then(r => { setSports(r.data.data.sports || []); setLevels(r.data.data.levels || []); });
  }, [orgId]);
  return { seasons, sports, levels };
}

/* ── Team Levels ─────────────────────────────────────────────── */
/* Level × Gender × Season × Variant dropdowns (AJ 2026-10-13). OL composes the
 * team name + every slug (roster, team, WP term) from the picks — no typing. Each
 * cell saves on change; "+ Add Level…" / "+ Add New…" extend the org's lists. */
const ADD = '__add__';
function LevelPickers({ v, onPick, testid, cfg, cell }) {
  const variantOpts = cfg.variants.map(n => ({ value: n.toLowerCase(), label: n }));
  // the squad's variant: in its code, or picked here for a squad whose code can't change
  const curVariant = (v.variant_label !== undefined && v.variant_label !== null) ? v.variant_label : (v.variant || '');
  if (curVariant && !variantOpts.some(o => o.value === curVariant)) {
    variantOpts.push({ value: curVariant, label: curVariant.charAt(0).toUpperCase() + curVariant.slice(1) });
  }
  return (
    <>
      <td className="px-1 py-1 min-w-[110px]">
        <select value={v.level_key || ''} className={cell} onChange={e => onPick('level_key', e.target.value)} data-testid={`${testid}-level`}>
          <option value="">Level…</option>
          {cfg.levels.map(l => <option key={l.key} value={l.key}>{l.name}</option>)}
          <option value={ADD}>+ Add Level…</option>
        </select>
      </td>
      <td className="px-1 py-1 min-w-[90px]">
        <select value={v.gender || ''} className={cell} onChange={e => onPick('gender', e.target.value)} data-testid={`${testid}-gender`}>
          <option value="">Gender…</option>
          {cfg.genders.map(g => <option key={g.key} value={g.key}>{g.name}</option>)}
        </select>
      </td>
      <td className="px-1 py-1 min-w-[90px]">
        <select value={v.term || ''} className={cell} onChange={e => onPick('term', e.target.value)} data-testid={`${testid}-term`}>
          <option value="">—</option>
          {cfg.terms.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
        </select>
      </td>
      <td className="px-1 py-1 min-w-[100px]">
        <select value={curVariant} className={cell} onChange={e => onPick('variant', e.target.value)} data-testid={`${testid}-variant`}>
          <option value="">—</option>
          {variantOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          <option value={ADD}>+ Add New…</option>
        </select>
      </td>
    </>
  );
}

function TeamLevels({ orgId, isDark, initial }) {
  const { seasons, sports } = useSeasonsSports(orgId);
  const [season, setSeason] = useState(initial?.season || '');
  const [sport, setSport] = useState(initial?.sport || '');
  const [rows, setRows] = useState(null);
  const [cfg, setCfg] = useState({ levels: [], genders: [], terms: [], variants: [] });
  const [draft, setDraft] = useState({ level_key: '', gender: '', term: '', variant: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!season && seasons.length) setSeason(seasons[0].label); }, [seasons, season]);
  useEffect(() => { if (!sport && sports.length) setSport(sports[0].value); }, [sports, sport]);
  const loadCfg = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/ad-teams/${orgId}/level-config`);
    setCfg(data.data);
  }, [orgId]);
  useEffect(() => { loadCfg(); }, [loadCfg]);
  const load = useCallback(async () => {
    if (!season || !sport) return;
    const { data } = await axios.get(`${API}/api/ad-teams/${orgId}/roster-teams`, { params: { season, sport } });
    setRows(data.data || []);
  }, [orgId, season, sport]);
  useEffect(() => { load(); }, [load]);
  const yearOf = () => (seasons.find(s => s.label === season) || {}).value;
  const sportLabel = () => (sports.find(s => s.value === sport) || {}).label || sport;
  const err = (e, fb) => window.alert(e?.response?.data?.detail || fb);

  /** "+ Add Level…" / "+ Add New…" → prompt, save to the org list, return the new key. */
  const addOption = async (kind) => {
    const name = window.prompt(kind === 'level' ? 'New level name (e.g. "Freshman")' : 'New variant (e.g. "Gold")');
    if (!name || !name.trim()) return null;
    try {
      const { data } = await axios.post(`${API}/api/ad-teams/${orgId}/${kind === 'level' ? 'levels' : 'variants'}`, { name: name.trim() });
      await loadCfg();
      return kind === 'level' ? data.data.key : String(data.data).toLowerCase();
    } catch (e) { err(e, 'Could not add'); return null; }
  };
  const pick = async (field, raw) => (raw === ADD ? addOption(field === 'level_key' ? 'level' : 'variant') : raw);

  const add = async () => {
    if (!draft.level_key || !draft.gender) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/ad-teams/${orgId}/roster-team`, { sport, year: yearOf(), ...draft });
      setDraft({ level_key: '', gender: '', term: '', variant: '' });
      load();
    } catch (e) { err(e, 'Create failed'); } finally { setBusy(false); }
  };
  const change = async (r, field, raw) => {
    const v = await pick(field, raw);
    const cur = field === 'variant'
      ? ((r.variant_label !== undefined && r.variant_label !== null) ? r.variant_label : (r.variant || ''))
      : (r[field] || '');
    if (v === null || v === cur) return;
    try {
      await axios.patch(`${API}/api/ad-teams/${orgId}/roster-team`, { slug: r.slug, [field]: v });
      load();
    } catch (e) { err(e, 'Cannot change this team'); load(); }
  };
  const del = async (r) => { if (!window.confirm(`Delete "${r.name}"?`)) return; try { await axios.delete(`${API}/api/ad-teams/${orgId}/roster-team/${r.slug}`); load(); } catch (e) { err(e, 'Cannot delete'); } };
  const copyPrev = async () => { const prev = seasons.find(s => s.label !== season); const to = yearOf(); const from = prev ? prev.value : String(parseInt(to, 10) - 1); if (!window.confirm(`Copy all ${sportLabel()} teams from ${from}-${parseInt(from, 10) + 1} into ${season}?`)) return; setBusy(true); try { const { data } = await axios.post(`${API}/api/ad-teams/${orgId}/copy-season`, { from_year: from, to_year: to }); window.alert(`Created ${data.created_count}, skipped ${data.skipped_count}.`); load(); } finally { setBusy(false); } };

  if (rows === null) return <Spin />;
  const cell = `w-full h-7 text-xs rounded px-1 border border-transparent hover:border-slate-300 focus:border-amber-500 bg-transparent ${isDark ? 'text-white hover:border-slate-600' : 'text-slate-900'}`;
  return (
    <Card className={wrap(isDark)}><CardContent className="p-4 space-y-3" data-testid="team-levels">
      <AgeCutoffControl orgId={orgId} isDark={isDark} />
      <div className="flex items-center gap-2 flex-wrap">
        <Sel value={season} onChange={setSeason} isDark={isDark} testid="levels-season" opts={seasons.map(s => ({ value: s.label, label: s.label }))} />
        <div className="flex gap-1">{sports.map(s => (
          <Button key={s.value} size="sm" variant={sport === s.value ? 'default' : 'outline'} className="h-8 text-xs" onClick={() => setSport(s.value)} data-testid={`levels-sport-${s.value}`}>{s.label}</Button>
        ))}</div>
      </div>
      {rows.length === 0 && (
        <div className="py-4 text-center text-sm">
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No {sportLabel()} teams for {season} yet.</p>
          <Button size="sm" className="mt-2 h-8 bg-amber-600 hover:bg-amber-700 text-white" onClick={copyPrev} disabled={busy} data-testid="levels-copy-prev">
            <Copy className="h-3.5 w-3.5 mr-1" />Copy from previous season</Button>
        </div>
      )}
      <table className="w-full text-sm border-collapse"><thead><tr>
        <th className={thCls(isDark)}>Level</th><th className={thCls(isDark)}>Gender</th>
        <th className={thCls(isDark)} title="Optional — for sports with more than one season a year">Season</th>
        <th className={thCls(isDark)} title="Optional — a second squad at the same level (White, Maroon…)">Variant</th>
        <th className={thCls(isDark)}>Team name (automatic)</th><th className={thCls(isDark)}>Players</th>
        <th className={thCls(isDark) + ' w-6'}></th>
      </tr></thead><tbody>
        {rows.map(r => (
          <tr key={r.slug} className={rowCls(isDark)} data-testid={`level-row-${r.slug}`}>
            <LevelPickers v={r} cfg={cfg} cell={cell} testid={`level-edit-${r.slug}`} onPick={(f, val) => change(r, f, val)} />
            <td className={tdCls + ' ' + (isDark ? 'text-amber-400' : 'text-amber-700')}>{r.name}</td>
            <td className={tdCls}>{r.count}</td>
            <td className={tdCls}><button onClick={() => del(r)} title="Delete" data-testid={`level-del-${r.slug}`} className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button></td>
          </tr>
        ))}
        <tr className={rowCls(isDark)} data-testid="level-add-row">
          <LevelPickers v={draft} cfg={cfg} cell={cell} testid="level-add" onPick={async (f, raw) => {
            const v = await pick(f, raw);
            if (v !== null) setDraft(d => ({ ...d, [f]: v }));
          }} />
          <td className="px-2 py-1" colSpan={3}>
            <Button size="sm" className="h-7 bg-amber-600 hover:bg-amber-700 text-white" disabled={busy || !draft.level_key || !draft.gender} onClick={add} data-testid="level-add-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" />Add team</>}</Button>
          </td>
        </tr>
      </tbody></table>
    </CardContent></Card>
  );
}

/* ── Team Costs ──────────────────────────────────────────────── */
// Also the AD's Finance ▸ Team Costs tab (AdFinancePanel) — one component, so the two never drift.
export function TeamCosts({ orgId, isDark, onGoLevels }) {
  const { seasons, sports } = useSeasonsSports(orgId);
  const [season, setSeason] = useState('');
  const [sport, setSport] = useState('');
  const [rows, setRows] = useState(null);
  useEffect(() => { if (!season && seasons.length) setSeason(seasons[0].label); }, [seasons, season]);
  useEffect(() => { if (!sport && sports.length) setSport(sports[0].value); }, [sports, sport]);
  const load = useCallback(async () => {
    if (!season || !sport) return;
    const { data } = await axios.get(`${API}/api/ad-teams/${orgId}/roster-teams`, { params: { season, sport } });
    setRows(data.data || []);
  }, [orgId, season, sport]);
  useEffect(() => { load(); }, [load]);
  const save = async (slug, field, val) => { await axios.post(`${API}/api/ad-teams/${orgId}/roster-cost`, { slug, [field]: val }); load(); };
  const sportLabel = () => (sports.find(s => s.value === sport) || {}).label || sport;
  if (rows === null) return <Spin />;
  return (
    <Card className={wrap(isDark)}><CardContent className="p-4 space-y-3" data-testid="team-costs">
      <div className="flex items-center gap-2 flex-wrap">
        <Sel value={season} onChange={setSeason} isDark={isDark} testid="costs-season" opts={seasons.map(s => ({ value: s.label, label: s.label }))} />
        <div className="flex gap-1">{sports.map(s => (
          <Button key={s.value} size="sm" variant={sport === s.value ? 'default' : 'outline'} className="h-8 text-xs" onClick={() => setSport(s.value)} data-testid={`costs-sport-${s.value}`}>{s.label}</Button>
        ))}</div>
        <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Costs are per team, per season.</span>
      </div>
      {rows.length === 0 ? (
        <div className="py-6 text-center text-sm" data-testid="costs-empty">
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No {sportLabel()} teams for {season} — add them in Team Levels.</p>
          <Button size="sm" className="mt-2 h-8 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => onGoLevels && onGoLevels(season, sport)} data-testid="costs-goto-levels">
            <Layers className="h-3.5 w-3.5 mr-1" />Go to Team Levels</Button>
        </div>
      ) : (
      <table className="w-full text-sm border-collapse"><thead><tr>
        <th className={thCls(isDark)}>Team</th><th className={thCls(isDark)}>Season cost ($)</th>
        <th className={thCls(isDark)}>Max age</th><th className={thCls(isDark)}>Players</th>
      </tr></thead><tbody>
        {rows.map(r => (
          <tr key={r.slug} className={rowCls(isDark)} data-testid={`cost-row-${r.slug}`}>
            <td className={tdCls + ' ' + (isDark ? 'text-amber-400' : 'text-amber-700')}>{r.name}</td>
            <td className={tdCls + ' min-w-[110px]'}><InlineText value={r.season_cost != null ? String(r.season_cost) : ''} type="number" placeholder="0" isDark={isDark} testid={`cost-${r.slug}`} onSave={v => save(r.slug, 'season_cost', parseFloat(v) || 0)} /></td>
            <td className={tdCls + ' min-w-[90px]'}><InlineText value={r.max_age != null ? String(r.max_age) : ''} type="number" placeholder="—" isDark={isDark} testid={`maxage-${r.slug}`} onSave={v => save(r.slug, 'max_age', parseInt(v, 10) || 0)} /></td>
            <td className={tdCls}>{r.count}</td>
          </tr>
        ))}
      </tbody></table>
      )}
    </CardContent></Card>
  );
}

/* ── small helpers ───────────────────────────────────────────── */
function Spin() { return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>; }
function Sel({ value, onChange, opts, isDark, testid, placeholder }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
            className={`h-8 text-sm rounded px-2 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
      {placeholder && <option value="">{placeholder}</option>}
      {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
